import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { formattaData } from '../utils/date';
import Layout from '../components/Layout';
import useAggiornamentoPeriodico from '../hooks/useAggiornamentoPeriodico';
import { usePuoScrivere } from '../utils/permessi';

export default function Comunicazioni() {
  const { utente } = useAuth();
  const puoScrivere = usePuoScrivere('intermittenti');
  const [comunicazioni, setComunicazioni] = useState([]);
  const [totale, setTotale] = useState(0);
  const [mostraForm, setMostraForm] = useState(false);
  const [dettaglio, setDettaglio] = useState(null);
  const [annullamentoInCorso, setAnnullamentoInCorso] = useState(null);
  const annullamentoRef = useRef(false);
  const [filtri, setFiltri] = useState({ stato: '', anno: new Date().getFullYear().toString(), mese: '', azienda_id: '' });
  const [aziende, setAziende] = useState([]);

  const caricaComunicazioni = useCallback(async () => {
    try {
      const params = {};
      if (filtri.stato) params.stato = filtri.stato;
      if (filtri.anno) params.anno = filtri.anno;
      if (filtri.mese) params.mese = filtri.mese;
      if (filtri.azienda_id) params.azienda_id = filtri.azienda_id;
      const res = await api.get('/comunicazioni', { params });
      setComunicazioni(res.data.comunicazioni);
      setTotale(res.data.totale);
    } catch (err) { console.error(err); }
  }, [filtri]);

  useEffect(() => { caricaComunicazioni(); }, [caricaComunicazioni]);
  useAggiornamentoPeriodico(caricaComunicazioni, { attivo: !mostraForm && !dettaglio });
  useEffect(() => {
    if (utente.ruolo === 'studio') api.get('/aziende').then(r => setAziende(r.data)).catch(() => {});
  }, [utente.ruolo]);

  const esportaExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filtri.stato) params.set('stato', filtri.stato);
      if (filtri.anno) params.set('anno', filtri.anno);
      if (filtri.mese) params.set('mese', filtri.mese);
      if (filtri.azienda_id) params.set('azienda_id', filtri.azienda_id);
      const res = await api.get(`/comunicazioni/export/excel?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'comunicazioni.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Errore nell\'esportazione'); }
  };

  const annullaComunicazione = async (id) => {
    if (!window.confirm('Sei sicuro di voler annullare questa comunicazione? Verrà inviata una revoca al Ministero.')) return;
    if (annullamentoRef.current) return;   // doppio clic: il pulsante si disabilita solo al ridisegno
    annullamentoRef.current = true;
    setAnnullamentoInCorso(id);
    try {
      await api.post(`/comunicazioni/${id}/annulla`);
      caricaComunicazioni();
    } catch (err) {
      alert(err.response?.data?.errore || 'Errore nell\'annullamento');
      if (err.response?.status === 409) caricaComunicazioni();   // qualcun altro l'ha già annullata: si mostra lo stato attuale
    } finally { annullamentoRef.current = false; setAnnullamentoInCorso(null); }
  };

  const statoBadge = (stato) => {
    const colori = { inviata: '#2e7d32', annullata: '#c00', errore: '#e65100' };
    return { background: colori[stato] || '#999', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem' };
  };

  const anni = []; for (let a = new Date().getFullYear(); a >= 2020; a--) anni.push(a);
  const mesi = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

  return (
    <Layout>
      <div style={styles.topBar}>
        <h2 style={{ margin: 0 }}>Comunicazioni ({totale})</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={esportaExcel} style={styles.btnSecondary}>Esporta Excel</button>
          {puoScrivere && <button onClick={() => setMostraForm(true)} style={styles.btn}>+ Nuova Comunicazione</button>}
        </div>
      </div>

      <div style={styles.filtri}>
        {utente.ruolo === 'studio' && (
          <select value={filtri.azienda_id} onChange={e => setFiltri(p => ({ ...p, azienda_id: e.target.value }))} style={styles.select}>
            <option value="">Tutte le aziende</option>
            {aziende.map(a => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
          </select>
        )}
        <select value={filtri.anno} onChange={e => setFiltri(p => ({ ...p, anno: e.target.value }))} style={styles.select}>
          <option value="">Tutti gli anni</option>
          {anni.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={filtri.mese} onChange={e => setFiltri(p => ({ ...p, mese: e.target.value }))} style={styles.select}>
          <option value="">Tutti i mesi</option>
          {mesi.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select value={filtri.stato} onChange={e => setFiltri(p => ({ ...p, stato: e.target.value }))} style={styles.select}>
          <option value="">Tutti gli stati</option>
          <option value="inviata">Inviata</option>
          <option value="annullata">Annullata</option>
          <option value="errore">Errore</option>
        </select>
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              {utente.ruolo === 'studio' && <th style={styles.th}>Azienda</th>}
              <th style={styles.th}>Lavoratore</th>
              <th style={styles.th}>Data Inizio</th>
              <th style={styles.th}>Data Fine</th>
              <th style={styles.th}>Stato</th>
              <th style={styles.th}>Inviata il</th>
              <th style={styles.th}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {comunicazioni.map(c => (
              <tr key={c.id} style={styles.tr}>
                {utente.ruolo === 'studio' && <td style={styles.td}>{c.ragione_sociale}</td>}
                <td style={styles.td}>{c.lav_cognome} {c.lav_nome}<br/><small style={{color:'#999'}}>{c.lav_cf}</small></td>
                <td style={styles.td}>{formattaData(c.data_inizio)}</td>
                <td style={styles.td}>{formattaData(c.data_fine)}</td>
                <td style={styles.td}><span style={statoBadge(c.stato)}>{c.stato.toUpperCase()}</span></td>
                <td style={styles.td}>{formattaData(c.created_at)}</td>
                <td style={styles.td}>
                  <button onClick={() => setDettaglio(c)} style={styles.btnSmall}>Dettagli</button>
                  {puoScrivere && c.stato === 'inviata' && !c.comunicazione_revoca_id && (
                    <button onClick={() => annullaComunicazione(c.id)} disabled={annullamentoInCorso === c.id} style={styles.btnSmallDanger}>
                      {annullamentoInCorso === c.id ? '...' : 'Annulla'}
                    </button>
                  )}
                  {c.comunicazione_revoca_id && <small style={{color:'#999', marginLeft:'0.3rem'}}>Revoca di #{c.comunicazione_revoca_id}</small>}
                  {c.revoca_id && <small style={{color:'#c00', marginLeft:'0.3rem'}}>Revocata</small>}
                </td>
              </tr>
            ))}
            {comunicazioni.length === 0 && (
              <tr><td colSpan={utente.ruolo === 'studio' ? 7 : 6} style={{ ...styles.td, textAlign: 'center', color: '#999' }}>Nessuna comunicazione trovata</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {mostraForm && <NuovaComunicazione utente={utente} onChiudi={() => setMostraForm(false)} onInviata={() => { setMostraForm(false); caricaComunicazioni(); }} />}
      {dettaglio && <DettaglioComunicazione comunicazione={dettaglio} onChiudi={() => setDettaglio(null)} />}
    </Layout>
  );
}

// Errore dell'invio. Se la prestazione risulta già inviata, spiega i passi per correggerla e reinviarla (il server non reinvia mai due volte la stessa).
function ErroreInvio({ errore, giaInviata, onChiudi }) {
  return (
    <div role="alert" style={styles.errore}>
      {errore}
      {giaInviata && (
        <div style={{ marginTop: '0.6rem', color: '#7f1d1d' }}>
          <strong>Cosa fare se va corretta:</strong>
          <ol style={{ margin: '0.3rem 0 0.5rem 1.2rem', padding: 0 }}>
            <li>Chiudi questa finestra.</li>
            <li>Nell'elenco, trova la riga di quel lavoratore con le stesse date e stato «Inviata».</li>
            <li>Premi «Annulla» sulla riga: viene inviata la revoca al Ministero e lo stato diventa «Annullata».</li>
            <li>Crea di nuovo la comunicazione con i dati corretti.</li>
          </ol>
          <button type="button" onClick={onChiudi} style={styles.btnSecondary}>Chiudi e vai all'elenco</button>
        </div>
      )}
    </div>
  );
}

function NuovaComunicazione({ utente, onChiudi, onInviata }) {
  const [lavoratori, setLavoratori] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [aziendaSel, setAziendaSel] = useState('');
  const [prestazioni, setPrestazioni] = useState([{ lavoratore_id: '', data_inizio: '', data_fine: '' }]);
  const [anteprima, setAnteprima] = useState(null);
  const [errore, setErrore] = useState('');
  const [giaInviata, setGiaInviata] = useState(false);   // il server ha risposto GIA_INVIATA: si mostra cosa fare
  const [invioInCorso, setInvioInCorso] = useState(false);

  useEffect(() => {
    if (utente.ruolo === 'studio') api.get('/aziende').then(r => setAziende(r.data)).catch(() => {});
  }, [utente.ruolo]);

  useEffect(() => {
    const params = {};
    if (utente.ruolo === 'studio' && aziendaSel) params.azienda_id = aziendaSel;
    if (utente.ruolo === 'studio' && !aziendaSel) return;
    api.get('/lavoratori/per-comunicazioni', { params }).then(r => setLavoratori(r.data)).catch(() => {});
  }, [utente.ruolo, aziendaSel]);

  const aggiornaPrestazione = (index, campo, valore) => {
    setPrestazioni(prev => prev.map((p, i) => i === index ? { ...p, [campo]: valore } : p));
  };

  const aggiungiRiga = () => {
    setPrestazioni(prev => [...prev, { lavoratore_id: '', data_inizio: '', data_fine: '' }]);
  };

  const rimuoviRiga = (index) => {
    setPrestazioni(prev => prev.filter((_, i) => i !== index));
  };

  const formValido = prestazioni.every(p => p.lavoratore_id && p.data_inizio && p.data_fine);

  const mostraAnteprimaFn = async () => {
    setErrore('');
    try {
      const res = await api.post('/comunicazioni/anteprima', { prestazioni });
      setAnteprima(`data:application/pdf;base64,${res.data.pdf}`);
    } catch (err) { setErrore(err.response?.data?.errore || 'Errore generazione anteprima'); }
  };

  const invioRef = useRef(false);
  const invia = async () => {
    if (invioRef.current) return;   // doppio clic: il pulsante si disabilita solo al ridisegno
    invioRef.current = true;
    setErrore(''); setGiaInviata(false); setInvioInCorso(true);
    try {
      await api.post('/comunicazioni/invia', { prestazioni });
      onInviata();
    } catch (err) {
      setErrore(err.response?.data?.errore || 'Errore invio comunicazione');
      setGiaInviata(err.response?.data?.codice === 'GIA_INVIATA');
    }
    finally { invioRef.current = false; setInvioInCorso(false); }
  };

  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.modal, maxWidth: anteprima ? '900px' : '700px' }}>
        {!anteprima ? (
          <>
            <h3>Nuova Comunicazione Obbligatoria Intermittenti</h3>
            {errore && <ErroreInvio errore={errore} giaInviata={giaInviata} onChiudi={onChiudi} />}

            {utente.ruolo === 'studio' && (
              <div style={styles.campo}>
                <label>Azienda</label>
                <select value={aziendaSel} onChange={e => { setAziendaSel(e.target.value); setPrestazioni([{ lavoratore_id: '', data_inizio: '', data_fine: '' }]); }} style={styles.input}>
                  <option value="">Seleziona azienda</option>
                  {aziende.map(a => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
                </select>
              </div>
            )}

            <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <strong style={{ fontSize: '0.9rem' }}>Elenco lavoratori - prestazioni lavorative</strong>
                <button type="button" onClick={aggiungiRiga} style={styles.btnSmall}>+ Aggiungi riga</button>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, padding: '0.5rem' }}>Lavoratore (C.F.)</th>
                    <th style={{ ...styles.th, padding: '0.5rem', width: '140px' }}>Data Inizio</th>
                    <th style={{ ...styles.th, padding: '0.5rem', width: '140px' }}>Data Fine</th>
                    <th style={{ ...styles.th, padding: '0.5rem', width: '40px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {prestazioni.map((p, i) => (
                    <tr key={i}>
                      <td style={{ padding: '0.3rem' }}>
                        <select value={p.lavoratore_id} onChange={e => aggiornaPrestazione(i, 'lavoratore_id', e.target.value)} style={styles.inputSmall}>
                          <option value="">Seleziona</option>
                          {lavoratori.map(l => <option key={l.id} value={l.id}>{l.cognome} {l.nome} — {l.codice_fiscale}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '0.3rem' }}>
                        <input type="date" value={p.data_inizio} onChange={e => aggiornaPrestazione(i, 'data_inizio', e.target.value)} style={styles.inputSmall} />
                      </td>
                      <td style={{ padding: '0.3rem' }}>
                        <input type="date" value={p.data_fine} onChange={e => aggiornaPrestazione(i, 'data_fine', e.target.value)} style={styles.inputSmall} />
                      </td>
                      <td style={{ padding: '0.3rem' }}>
                        {prestazioni.length > 1 && (
                          <button type="button" onClick={() => rimuoviRiga(i)} style={{ ...styles.btnSmallDanger, padding: '0.2rem 0.5rem' }}>×</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={styles.modalActions}>
              <button type="button" onClick={onChiudi} style={styles.btnSecondary}>Annulla</button>
              <button type="button" onClick={mostraAnteprimaFn} disabled={!formValido} style={styles.btnSecondary}>Anteprima</button>
              <button type="button" onClick={invia} disabled={!formValido || invioInCorso} style={styles.btn}>
                {invioInCorso ? 'Invio in corso...' : 'Invia al Ministero'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Anteprima Modulo ML-15-01</h3>
              <button onClick={() => setAnteprima(null)} style={styles.btnSmall}>Torna al form</button>
            </div>
            {errore && <ErroreInvio errore={errore} giaInviata={giaInviata} onChiudi={onChiudi} />}
            <iframe src={anteprima} title="Anteprima PDF" style={{ width: '100%', height: '60vh', border: '1px solid #ddd', borderRadius: '6px' }} />
            <div style={styles.modalActions}>
              <button onClick={() => setAnteprima(null)} style={styles.btnSecondary}>Modifica</button>
              <button onClick={invia} disabled={invioInCorso} style={styles.btn}>{invioInCorso ? 'Invio in corso...' : 'Conferma e Invia'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DettaglioComunicazione({ comunicazione, onChiudi }) {
  const scaricaPDF = async () => {
    try {
      const res = await api.get(`/comunicazioni/${comunicazione.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `comunicazione_${comunicazione.id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('PDF non disponibile'); }
  };

  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.modal, maxWidth: '600px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Dettaglio Comunicazione #{comunicazione.id}</h3>
          <span style={{ fontSize: '0.85rem', color: '#666' }}>Stato: <strong>{comunicazione.stato.toUpperCase()}</strong></span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem', fontSize: '0.9rem' }}>
          <div><strong>Lavoratore:</strong> {comunicazione.lav_cognome} {comunicazione.lav_nome}</div>
          <div><strong>C.F.:</strong> {comunicazione.lav_cf}</div>
          <div><strong>Data Inizio:</strong> {formattaData(comunicazione.data_inizio)}</div>
          <div><strong>Data Fine:</strong> {formattaData(comunicazione.data_fine)}</div>
          <div><strong>Inviata a:</strong> {comunicazione.email_inviata_a}</div>
          <div><strong>CC:</strong> {comunicazione.email_cc}</div>
          {comunicazione.errore_dettaglio && <div style={{ gridColumn: '1/3', color: '#c00' }}><strong>Errore:</strong> {comunicazione.errore_dettaglio}</div>}
        </div>
        <div style={styles.modalActions}>
          <button onClick={scaricaPDF} style={styles.btnSecondary}>Scarica PDF</button>
          <button onClick={onChiudi} style={styles.btn}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' },
  filtri: { display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' },
  select: { padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' },
  tableWrapper: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  th: { textAlign: 'left', padding: '0.8rem', background: '#f8f9fa', borderBottom: '2px solid #eee', fontSize: '0.85rem', color: '#555' },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '0.7rem 0.8rem', fontSize: '0.9rem' },
  btn: { background: '#232323', color: 'white', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' },
  btnSecondary: { background: 'white', color: '#333', border: '1px solid #ddd', padding: '0.6rem 1.2rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' },
  btnSmall: { background: '#eef', color: '#232323', border: 'none', padding: '0.3rem 0.7rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', marginRight: '0.3rem' },
  btnSmallDanger: { background: '#fee', color: '#c00', border: 'none', padding: '0.3rem 0.7rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modal: { background: 'white', padding: '2rem', borderRadius: '12px', width: '100%', maxHeight: '90vh', overflow: 'auto' },
  campo: { marginBottom: '1rem', flex: 1 },
  input: { width: '100%', padding: '0.6rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem', boxSizing: 'border-box', marginTop: '0.3rem' },
  inputSmall: { width: '100%', padding: '0.4rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' },
  row: { display: 'flex', gap: '1rem' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' },
  errore: { background: '#fee', color: '#c00', padding: '0.7rem', borderRadius: '6px', marginBottom: '1rem' },
};
